import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  KeyboardAvoidingView,
  type NativeScrollEvent,
  Platform,
  type ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native"
import StretchyHeader, {
  HEADER_HEIGHT,
} from "@/components/analysis/header/StretchyHeader"
import CircularFloatingButton from "@/components/buttons/CircularFloatingButton"
import CompsAndCalculateTabs from "@/components/analysis/CompsAndCalculateTabs"
import { BFColors } from "@/constants/Colors"
import { BottomSheetModal } from "@gorhom/bottom-sheet"
import { useAnalysis, useEditPropertyFeatures } from "@/queries/analysis"
import { type AnalysisType, type UserType } from "@/generated/graphql"
import PropertyEditModal from "@/components/analysis/modal/PropertyEditModal"
import { router } from "expo-router"
import { type AnalysisEditInput } from "@/types/analysisTypes"
import FullScreenLoader from "@/components/loading/FullScreenLoader"
import { useUser } from "@/queries/user"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { useViewedCallback } from "@/analytics/react"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { SmallStickyPropertyHeader } from "@/components/analysis/header/SmallStickyPropertyHeader"
import {
  AnalyzeProvider,
  useAnalyzeContext,
} from "@/components/analysis/AnalyzeContext"
import { useIsMutating } from "@tanstack/react-query"
import { useScreenName } from "@/hooks/useScreenName"
import {
  customizeCompsButtonClicked,
  findCompsButtonClicked,
  sortCompsButtonClicked,
} from "@/analytics/events/analyzer"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useCopilot } from "react-native-copilot"
import ReusableSortPopup from "@/components/sorting/ReusableSortPopup"
import {
  isCurrentSortReversed,
  updateSorting,
  currentCriterionStore,
} from "@/stores/compSortingDataStore"
import { useStore } from "@nanostores/react"
import {
  type CompSortCriterion,
  type CompSortOrder,
  sortOptions,
} from "@/types/compTypes"
import useCompSortingSections from "@/hooks/useCompSortingSections"
import { scrollToRecentStore } from "@/stores/compsDataStore"

interface AnalysisContentProps {
  analysis: AnalysisType | null
  isAnalysisLoading: boolean
  isEditPending: boolean
  isStickyHeaderActive: boolean
  setIsStickyHeaderActive: (active: boolean) => void
  onEditPress: () => void
}

const AnalysisContent: React.FC<
  AnalysisContentProps & { scrollViewRef: React.RefObject<ScrollView> }
> = ({
  analysis,
  isAnalysisLoading,
  isEditPending,
  isStickyHeaderActive,
  setIsStickyHeaderActive,
  onEditPress,
  scrollViewRef,
}) => {
  const { data: user } = useUser()
  const scrollY = useRef(new Animated.Value(0))
  const { start, copilotEvents, currentStep } = useCopilot()
  const { isFirstManualAnalysisDisplay, isTourActive, setIsTourActive } =
    useAnalyzeContext()
  const [tourStarted, setTourStarted] = useState(false)
  const { height: screenHeight } = useWindowDimensions()

  useEffect(() => {
    // EN-4550
    const heightWhereStepIsNoLongerVisible: number = 713
    const additionalSafeScrollAmount: number =
      screenHeight < heightWhereStepIsNoLongerVisible ? 90 : 0
    if (
      isTourActive &&
      (currentStep?.order === 2 || currentStep?.order === 3)
    ) {
      // Scroll up the ScrollView when the tour is active
      scrollViewRef.current?.scrollTo({
        y: 125 + additionalSafeScrollAmount,
        animated: false,
      })
    } else {
      scrollViewRef.current?.scrollTo({ y: 0, animated: false })
    }
  }, [isTourActive, currentStep])

  const isMutatingAnalysis = useIsMutating({
    mutationKey: ["analysis", "compPreferences"],
  })

  const handleScroll = Animated.event<NativeScrollEvent>(
    [{ nativeEvent: { contentOffset: { y: scrollY.current } } }],
    {
      useNativeDriver: Platform.select({
        default: true,
        web: false,
      }),
      listener: (event) => {
        const { y: yOffset } = event.nativeEvent.contentOffset
        const isSticky = yOffset > 100
        setIsStickyHeaderActive(isSticky)
      },
    }
  )

  const onScrollEnd = (event: {
    nativeEvent: { contentOffset: { y: any } }
  }) => {
    const yOffset = event.nativeEvent.contentOffset.y
    // Record the scroll position using your analytics event
    AnalyzerEvents.fullScreenCompListScrolled({
      scrolledTo: Math.ceil(yOffset / HEADER_HEIGHT) + 1,
    })
  }

  // Tour Walkthrough
  useEffect(() => {
    if (!isFirstManualAnalysisDisplay) {
      return
    }
    if (!tourStarted) {
      const timer = setTimeout(() => {
        AnalyzerEvents.compSelectionWalkthroughStarted(analysis)
        start()
          .then(() => {
            copilotEvents.on("stop", handleStop) // Listen for tapping outside of Tour
            setTourStarted(true)
            setIsTourActive(true) // Update the context to reflect that the tour is active
          })
          .catch((error) => {
            console.error("Failed to start Copilot tour:", error)
            setTourStarted(false) // Allow retry if the tour failed to start
          })
      }, 500)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [
    isFirstManualAnalysisDisplay,
    start,
    isTourActive,
    setIsTourActive,
    tourStarted,
  ])

  const handleStop = () => {
    setIsTourActive(false)
  }

  let loaderText = null
  if (isAnalysisLoading) {
    loaderText = "Finding comps and calculating ARV..."
  } else if (isEditPending) {
    loaderText = "Updating Edits..."
  } else if (isMutatingAnalysis) {
    loaderText = "Updating"
  }

  if (loaderText) {
    return <FullScreenLoader>{loaderText}</FullScreenLoader>
  }

  return (
    <>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
        enabled
      >
        <Animated.ScrollView
          style={styles.scrollView}
          ref={scrollViewRef}
          scrollEventThrottle={16}
          onScroll={handleScroll}
          onMomentumScrollEnd={onScrollEnd}
          onScrollEndDrag={onScrollEnd}
        >
          {analysis && (
            <StretchyHeader
              scrollY={scrollY.current}
              user={user?.me as UserType}
              analysis={analysis}
              compactHeaderIsActive={isStickyHeaderActive}
              onEditPress={onEditPress}
              onStreetPress={() => {
                router.push(`/properties/${analysis?.id}/street`)
              }}
            />
          )}
          <View className="bg-off-white" pointerEvents={"auto"}>
            <CompsAndCalculateTabs analysis={analysis} />
          </View>
        </Animated.ScrollView>
      </KeyboardAvoidingView>
      <SmallStickyPropertyHeader
        analysis={analysis}
        scrollY={scrollY.current}
      />
      {!isTourActive && (
        <CircularFloatingButton
          analysisId={analysis?.id!}
          type="getLoan"
          style={{
            bottom: 20,
            right: 20,
            position: "absolute",
          }}
        />
      )}
    </>
  )
}

interface SubjectPropertyScreenProps {
  analysisId: string
  onBackButtonChange: (isDark: boolean) => void
}

const SubjectPropertyScreen: React.FC<SubjectPropertyScreenProps> = ({
  analysisId,
  onBackButtonChange,
}) => {
  const [analysis, setAnalysis] = useState<AnalysisType | null>(null)
  const editPropertyModalRef = useRef<BottomSheetModal>(null)
  const [isStickyHeaderActive, setIsStickyHeaderActive] = useState(false)
  const {
    data: analysisQuery,
    refetch,
    isLoading: isAnalysisLoading,
  } = useAnalysis(analysisId)
  const { mutate: editPropertyFeaturesMutation, isPending: isEditPending } =
    useEditPropertyFeatures()
  const screenName = useScreenName()
  const isDesktop = useIsDesktop()
  const [sortModalVisible, setSortModalVisible] = useState(false)
  const compSortingSections = useCompSortingSections()
  const selectedSortingCriterion = useStore(currentCriterionStore)
  const isSortReversed = useStore(isCurrentSortReversed)
  const scrollViewRef = useRef<ScrollView>(null) // Ref for the ScrollView
  const scrollToRecent = useStore(scrollToRecentStore)
  const { height: screenHeight } = useWindowDimensions()

  useEffect(() => {
    if (scrollToRecent) {
      scrollToMostRecentComp()
      // Reset the store to prevent repeated scrolling
      scrollToRecentStore.set(false)
    }
  }, [scrollToRecent])

  const scrollToMostRecentComp = useCallback(() => {
    console.log(`*Scrolling...*`)
    scrollViewRef.current?.scrollTo({
      y: screenHeight - 290,
      animated: true,
    })
  }, [scrollViewRef])

  useEffect(() => {
    setAnalysis(null)
  }, [analysisId])

  useEffect(() => {
    if (analysisQuery?.analysis) {
      setAnalysis(analysisQuery?.analysis as AnalysisType)
    }
  }, [analysisQuery])

  useViewedCallback(() => {
    setStatusBarStyle("light")
  })

  useEffect(() => {
    setStatusBarStyle(isStickyHeaderActive ? "dark" : "light")
    onBackButtonChange(isStickyHeaderActive)
    if (isStickyHeaderActive && analysisQuery?.analysis) {
      AnalyzerEvents.compactCompListViewed({
        analysis: analysisQuery?.analysis as AnalysisType,
        fromScreen: "Subject Property Comps",
      })
    }
  }, [isStickyHeaderActive])

  const onEditProperty = (
    bedrooms: number,
    bathrooms: number,
    area: number,
    purchasePrice?: number
  ) => {
    const analysisInput: AnalysisEditInput = {
      bedrooms,
      bathrooms,
      area,
    }

    if (purchasePrice !== null) {
      analysisInput.purchasePrice = purchasePrice
    }

    editPropertyFeaturesMutation(
      {
        analysisId: analysis?.id!,
        analysisInput,
      },
      {
        onSuccess: () => {
          void refetch()
        },
      }
    )
    editPropertyModalRef.current?.dismiss()
  }

  const onCustomCompsPress = useCallback(() => {
    customizeCompsButtonClicked({
      currentScreen: screenName,
      toScreen: "Customize Comps",
    })
    router.push(`/properties/${analysisId}/customizecomps`)
  }, [analysisId])

  const onSortCompsPress = useCallback(() => {
    setSortModalVisible(!sortModalVisible)
    sortCompsButtonClicked({
      currentScreen: screenName,
      toScreen: "Subject Property",
    })
  }, [analysisId, sortModalVisible])

  const onFindCompsPress = useCallback(() => {
    findCompsButtonClicked({
      currentScreen: screenName,
      toScreen: "Search Comps",
    })
    router.push(
      `/properties/${analysisId}/searchcomps/${analysisQuery?.analysis?.property.placeId}/`
    )
  }, [analysisId])

  // TODO: This should be handled from the BE
  const isReversedByDefault = (criterion: CompSortCriterion): boolean => {
    const criterionOrder = sortOptions[criterion]?.order || "default"
    return criterionOrder === "reverse"
  }

  return (
    <AnalyzeProvider
      analysisId={analysisId}
      analysis={analysis}
      onCustomCompsPress={onCustomCompsPress}
      onSortCompsPress={onSortCompsPress}
      onFindCompsPress={onFindCompsPress}
    >
      <View style={styles.container}>
        <AnalysisContent
          analysis={analysis}
          isAnalysisLoading={isAnalysisLoading}
          isEditPending={isEditPending}
          isStickyHeaderActive={isStickyHeaderActive}
          setIsStickyHeaderActive={setIsStickyHeaderActive}
          onEditPress={() => {
            editPropertyModalRef.current?.present()
          }}
          scrollViewRef={scrollViewRef} // Pass scrollViewRef to AnalysisContent
        />

        <ReusableSortPopup
          displays={sortModalVisible}
          sort={selectedSortingCriterion}
          direction={isSortReversed ? "reverse" : "default"}
          isReversedByDefault={isReversedByDefault(selectedSortingCriterion)}
          sections={compSortingSections}
          positionClassName="right-8 top-[210px]" // Pass positioning
          onSelectSort={(sortCriterion) => {
            updateSorting(sortCriterion as CompSortCriterion)
            setSortModalVisible(false)
          }}
          onSelectDirection={(sortOrder: CompSortOrder) => {
            updateSorting(selectedSortingCriterion, sortOrder)
            setSortModalVisible(false)
          }}
          onClose={() => {
            setSortModalVisible(false)
          }}
        />

        {analysis && (
          <BottomSheetModal
            ref={editPropertyModalRef}
            index={0}
            snapPoints={[isDesktop ? "85%" : "99%"]}
            handleIndicatorStyle={{ backgroundColor: BFColors.lightGrey }}
            enableDynamicSizing={false}
          >
            <PropertyEditModal
              analysisId={analysisId}
              street={analysis?.property.street}
              cityStateZip={`${analysis.property.city}, ${analysis.property.state} ${analysis.property.zipcode}`}
              onEditProperty={onEditProperty}
              isRequiredEdit={false}
              onClose={() => {
                editPropertyModalRef.current?.dismiss()
              }}
            />
          </BottomSheetModal>
        )}
      </View>
    </AnalyzeProvider>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  scrollView: {
    flex: 1,
  },
})

export default SubjectPropertyScreen
